import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { CloseOutlined } from "@ant-design/icons";

import BaseButton from "@/components_v2/Button/BaseButton";
import SurveyDataBlockModal from "@/features/survey/form/components/SurveyDataBlockModal";
import {
  EntitiesCondition,
  SAMPLE_SURVEY_PATH_PARAM_VALUE,
} from "@/shared/utils/constants.js";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import AddDocumentIcon from "@/assets/icons/add-document.svg?react";
import SurveyIcon from "@/assets/icons/survey-icon.svg?react";
import { getEvidenceTitle } from "@/domains/evidence/hooks/useEvidenceFields";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import { OptionEntitiesProps } from "@/features/survey/form/types";
import { DataBlockListResponse } from "@/redux/datablocks/types";

const Evidence = ({ option, answer, handleSubmit }: OptionEntitiesProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const isSample = location.pathname.includes(SAMPLE_SURVEY_PATH_PARAM_VALUE);

  const [dataBlocksModelOpen, setDataBlocksModelOpen] = useState(false);

  const handleRemoveBlock = async (blockId: number) => {
    const newBlocks = answer.attachedBlocks.filter((i) => i.id !== blockId);
    handleSubmit(newBlocks);
  };

  const handleAttachBlocks = async (blocks: DataBlockListResponse[]) => {
    await handleSubmit(blocks);
    setDataBlocksModelOpen(false);
  };

  const requiredDataBlockTypes = useMemo(() => {
    if (option.optionEntities.length > 0) {
      return option.optionEntities.map((i) => i.dataTemplate.title);
    }
    return [t("survey.labels.all")];
  }, [option]);

  const requiredDataTemplateIds = useMemo(() => {
    return option.optionEntities.map((i) => i.dataTemplate.id);
  }, [option]);

  return (
    <div className="option-entities">
      {requiredDataBlockTypes && (
        <p className="option-entities-label body-m-regular">
          <BaseSvgRenderer component={SurveyIcon} width="16" height="16" />
          {t(
            option.entitiesCondition === EntitiesCondition.AND.value
              ? "survey.labels.dataBlockTypesRequired"
              : "survey.labels.dataBlockTypesAccepted",
            {
              plural: requiredDataBlockTypes.length === 1 ? "" : "s",
            },
          )}{" "}
          {requiredDataBlockTypes.join(", ")}
        </p>
      )}
      {!isSample && (
        <BaseButton
          size={BUTTON_SIZES.SMALL}
          leadingIcon={
            <BaseSvgRenderer component={AddDocumentIcon} stroke="white" />
          }
          onClick={() => setDataBlocksModelOpen(true)}
        >
          {t("survey.labels.addEvidence")}
        </BaseButton>
      )}

      {answer && answer.attachedBlocks.length > 0 ? (
        <div className="option-entity-attached-container">
          <h4 className="option-entity-attached-header-label body-m-regular">
            {t("survey.labels.attachedEvidence")} (
            {answer.attachedBlocks.length})
          </h4>
          {answer.attachedBlocks.map((block) => {
            const title = getEvidenceTitle(block);
            return (
              <div
                key={block.id}
                className="option-entity-attached-content-container"
              >
                <Link to={`/evidence/${block.id}`}>
                  <h4 className="option-entity-attached-item body-m-medium">
                    {title}
                  </h4>
                </Link>
                {!isSample && (
                  <BaseButton
                    size={BUTTON_SIZES.SMALL}
                    variant={BUTTON_VARIANTS.LINK}
                    onClick={() => handleRemoveBlock(block.id)}
                  >
                    <CloseOutlined />
                  </BaseButton>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
      <SurveyDataBlockModal
        open={dataBlocksModelOpen}
        setOpen={setDataBlocksModelOpen}
        initialData={{
          question: option.question,
        }}
        filters={{
          dataTemplates: requiredDataTemplateIds,
          prioritizeQuestion: option.question,
        }}
        selectedBlocks={answer?.attachedBlocks || []}
        setSelectedBlocks={handleAttachBlocks}
      />
    </div>
  );
};

export default Evidence;
